import { Settings } from 'lucide-react';

import { actionColor, actionLabel, type AuditAction, type AuditRow } from '../auditViewModel';
import { avatarColorFor } from '../roleTaxonomy';

import UserAvatar from './UserAvatar';

interface AuditSidebarProps {
  rows: AuditRow[];
}

function ActionBreakdown({ rows }: { rows: AuditRow[] }) {
  const counts = new Map<AuditAction, number>();
  for (const r of rows) counts.set(r.action, (counts.get(r.action) ?? 0) + 1);
  const max = Math.max(1, ...Array.from(counts.values()));

  if (counts.size === 0) {
    return (
      <Card title="Action breakdown" sub="this period">
        <div className="text-[11.5px] text-stone-500 dark:text-stone-400">
          No events to break down yet.
        </div>
      </Card>
    );
  }

  return (
    <Card title="Action breakdown" sub="this period">
      <div className="flex flex-col gap-1.5">
        {Array.from(counts.entries()).map(([action, n]) => (
          <div key={action} className="flex items-center gap-2">
            <i
              className="w-1.5 h-1.5 rounded-full flex-none"
              style={{ background: actionColor(action) }}
            />
            <span className="flex-1 text-[11.5px] text-bark-900 dark:text-[#e8ece9] font-medium">
              {actionLabel(action)}
            </span>
            <div className="w-[60px] h-1 rounded-full bg-stone-100 dark:bg-[#242d28] overflow-hidden">
              <div
                className="h-full"
                style={{ width: `${(n / max) * 100}%`, background: actionColor(action) }}
              />
            </div>
            <span className="w-4 text-right text-[11.5px] font-bold tabular-nums">{n}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function TopActors({ rows }: { rows: AuditRow[] }) {
  const counts = new Map<string, number>();
  for (const r of rows) counts.set(r.actor, (counts.get(r.actor) ?? 0) + 1);
  const arr = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
  const max = arr[0]?.[1] ?? 1;

  if (arr.length === 0) {
    return (
      <Card title="Top actors" sub="by events">
        <div className="text-[11.5px] text-stone-500 dark:text-stone-400">No actors yet.</div>
      </Card>
    );
  }

  return (
    <Card title="Top actors" sub="by events">
      <div className="flex flex-col gap-2">
        {arr.map(([actor, n]) => (
          <div key={actor} className="flex items-center gap-2">
            {actor === 'system' || actor === 'System' ? (
              <span className="w-[22px] h-[22px] rounded-full bg-stone-100 dark:bg-[#242d28] text-stone-500 dark:text-stone-400 inline-flex items-center justify-center">
                <Settings size={12} />
              </span>
            ) : (
              <UserAvatar name={actor} size={22} color={avatarColorFor(actor)} />
            )}
            <span className="flex-1 text-[12px] font-medium truncate">{actor}</span>
            <div className="w-[50px] h-1 rounded-full bg-stone-100 dark:bg-[#242d28] overflow-hidden">
              <div className="h-full bg-moss-600" style={{ width: `${(n / max) * 100}%` }} />
            </div>
            <span className="w-4 text-right text-[11.5px] font-bold tabular-nums">{n}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Card({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white dark:bg-[#1a1f1c] rounded-xl border border-stone-200 dark:border-stone-800 p-4">
      <div className="flex items-center justify-between mb-2.5">
        <h3 className="m-0 text-[13px] font-bold uppercase tracking-wider text-bark-900 dark:text-[#e8ece9]">
          {title}
        </h3>
        {sub && (
          <span className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400">
            {sub}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

/** Right column of the audit page: action breakdown + top actors. */
export default function AuditSidebar({ rows }: AuditSidebarProps) {
  return (
    <div className="flex flex-col gap-3.5 min-w-0 min-h-0">
      <ActionBreakdown rows={rows} />
      <TopActors rows={rows} />
    </div>
  );
}
